import { create } from "zustand";

interface UnsavedKey {
  isDirty: boolean;
  /** Friendly label rendered in the unsaved-changes dialog. */
  label: string;
}

interface UnsavedChangesState {
  // Dirty registry
  dirtyKeys: Record<string, UnsavedKey>;
  setDirty: (key: string, isDirty: boolean, label?: string) => void;
  clearKey: (key: string) => void;
  clearAll: () => void;

  // Confirm-dialog state (single instance, deduped)
  confirmOpen: boolean;
  pendingAction: (() => void) | null;
  /**
   * Run `action` immediately if nothing is dirty; otherwise open the dialog
   * and only run on user confirmation. Subsequent calls while the dialog is
   * already open are dropped — prevents the multiple-open bug.
   */
  requestConfirm: (action: () => void) => void;
  resolveConfirm: (proceed: boolean) => void;

  /**
   * Monotonic counter incremented each time the user clicks "Stay on this
   * page" in the unsaved-changes dialog. Dirty inputs subscribe to bumps so
   * they can flash their highlight, drawing attention to where edits live.
   */
  highlightNonce: number;
  bumpHighlight: () => void;
}

/**
 * Derive a default label from a key like `weight-energy-target:abc-123` →
 * "Weight Energy Target". Used when callers don't pass an explicit label.
 */
const deriveLabel = (key: string) => {
  const head = key.split(":")[0] ?? key;
  return head
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const useUnsavedChangesStore = create<UnsavedChangesState>(
  (set, get) => ({
    dirtyKeys: {},
    setDirty: (key, isDirty, label) =>
      set((state) => {
        const current = state.dirtyKeys[key];
        const nextLabel = label ?? current?.label ?? deriveLabel(key);
        if (
          current &&
          current.isDirty === isDirty &&
          current.label === nextLabel
        ) {
          return state;
        }
        const next = { ...state.dirtyKeys };
        if (isDirty) next[key] = { isDirty: true, label: nextLabel };
        else delete next[key];
        return { dirtyKeys: next };
      }),
    clearKey: (key) =>
      set((state) => {
        if (!(key in state.dirtyKeys)) return state;
        const next = { ...state.dirtyKeys };
        delete next[key];
        return { dirtyKeys: next };
      }),
    clearAll: () => set({ dirtyKeys: {} }),

    confirmOpen: false,
    pendingAction: null,
    requestConfirm: (action) => {
      const state = get();
      const dirtyCount = Object.keys(state.dirtyKeys).length;
      if (dirtyCount === 0) {
        action();
        return;
      }
      // Already showing — drop the new request rather than queueing or
      // triggering a second dialog.
      if (state.confirmOpen) return;
      set({ confirmOpen: true, pendingAction: action });
    },
    resolveConfirm: (proceed) => {
      const action = get().pendingAction;
      // Closing the dialog clears state regardless of choice.
      set({ confirmOpen: false, pendingAction: null });
      if (proceed) {
        // User chose to leave — clear the registry and run the queued action.
        set({ dirtyKeys: {} });
        action?.();
      } else {
        // Stayed — flash dirty inputs so the user can find them.
        set((s) => ({ highlightNonce: s.highlightNonce + 1 }));
      }
    },

    highlightNonce: 0,
    bumpHighlight: () => set((s) => ({ highlightNonce: s.highlightNonce + 1 })),
  })
);

export const hasAnyUnsaved = () =>
  Object.keys(useUnsavedChangesStore.getState().dirtyKeys).length > 0;

export const getDirtyLabels = () =>
  Object.values(useUnsavedChangesStore.getState().dirtyKeys).map(
    (k) => k.label
  );
